// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Offline storage module using IndexedDB for PWA functionality.
 * Provides persistent storage for game state and queued actions.
 */

import { logger } from './logger';
import type { GameState, CardsListResponse } from '../types/game';

const DB_NAME = 'undef-offline';
const DB_VERSION = 1;

// Store names
const STORES = {
  GAME_STATE: 'gameState',
  QUEUED_ACTIONS: 'queuedActions',
  CARD_DATA: 'cardData',
  USER_PREFERENCES: 'userPreferences',
} as const;

export interface QueuedGameAction {
  id: string;
  timestamp: number;
  action: string;
  payload: Record<string, unknown>;
  sessionId: string;
  playerId: string;
  retryCount: number;
}

interface UserPreferences {
  key: string;
  value: unknown;
  updatedAt: number;
}

class OfflineStorage {
  private db: IDBDatabase | null = null;
  private dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Initialize the IndexedDB database
   */
  async init(): Promise<void> {
    if (this.db) return;

    if (!this.dbPromise) {
      this.dbPromise = this.openDatabase();
    }

    this.db = await this.dbPromise;
  }

  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB not supported'));
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        logger.error('Failed to open IndexedDB', { error: request.error });
        reject(request.error);
      };

      request.onsuccess = () => {
        logger.info('IndexedDB opened successfully');
        resolve(request.result);
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create game state store
        if (!db.objectStoreNames.contains(STORES.GAME_STATE)) {
          db.createObjectStore(STORES.GAME_STATE, { keyPath: 'sessionId' });
        }

        // Create queued actions store with auto-increment
        if (!db.objectStoreNames.contains(STORES.QUEUED_ACTIONS)) {
          const actionStore = db.createObjectStore(STORES.QUEUED_ACTIONS, {
            keyPath: 'id',
          });
          actionStore.createIndex('timestamp', 'timestamp', { unique: false });
          actionStore.createIndex('sessionId', 'sessionId', { unique: false });
        }

        // Create card data store
        if (!db.objectStoreNames.contains(STORES.CARD_DATA)) {
          db.createObjectStore(STORES.CARD_DATA, { keyPath: 'type' });
        }

        // Create user preferences store
        if (!db.objectStoreNames.contains(STORES.USER_PREFERENCES)) {
          db.createObjectStore(STORES.USER_PREFERENCES, { keyPath: 'key' });
        }

        logger.info('IndexedDB schema upgraded', { version: DB_VERSION });
      };
    });
  }

  /**
   * Save game state for a session
   */
  async saveGameState(sessionId: string, state: GameState): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.GAME_STATE], 'readwrite');
      const store = transaction.objectStore(STORES.GAME_STATE);

      const data = {
        sessionId,
        state,
        savedAt: Date.now(),
      };

      const request = store.put(data);

      request.onsuccess = () => {
        logger.debug('Game state saved', { sessionId });
        resolve();
      };

      request.onerror = () => {
        logger.error('Failed to save game state', { error: request.error });
        reject(request.error);
      };
    });
  }

  /**
   * Load game state for a session
   */
  async loadGameState(sessionId: string): Promise<GameState | null> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.GAME_STATE], 'readonly');
      const store = transaction.objectStore(STORES.GAME_STATE);
      const request = store.get(sessionId);

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          logger.debug('Game state loaded', {
            sessionId,
            savedAt: result.savedAt,
          });
          resolve(result.state);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        logger.error('Failed to load game state', { error: request.error });
        reject(request.error);
      };
    });
  }

  /**
   * Clear game state for a session
   */
  async clearGameState(sessionId: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.GAME_STATE], 'readwrite');
      const store = transaction.objectStore(STORES.GAME_STATE);
      const request = store.delete(sessionId);

      request.onsuccess = () => {
        logger.debug('Game state cleared', { sessionId });
        resolve();
      };

      request.onerror = () => {
        logger.error('Failed to clear game state', { error: request.error });
        reject(request.error);
      };
    });
  }

  /**
   * Queue an action for later sync
   */
  async queueAction(action: Omit<QueuedGameAction, 'id' | 'retryCount'>): Promise<string> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    const id = `${action.sessionId}-${action.timestamp}-${Math.random().toString(36).substr(2, 9)}`;

    const queuedAction: QueuedGameAction = {
      ...action,
      id,
      retryCount: 0,
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.QUEUED_ACTIONS], 'readwrite');
      const store = transaction.objectStore(STORES.QUEUED_ACTIONS);
      const request = store.add(queuedAction);

      request.onsuccess = () => {
        logger.info('Action queued for sync', { id, action: action.action });
        resolve(id);
      };

      request.onerror = () => {
        logger.error('Failed to queue action', { error: request.error });
        reject(request.error);
      };
    });
  }

  /**
   * Get all queued actions
   */
  async getQueuedActions(): Promise<QueuedGameAction[]> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.QUEUED_ACTIONS], 'readonly');
      const store = transaction.objectStore(STORES.QUEUED_ACTIONS);
      const index = store.index('timestamp');
      const request = index.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        logger.error('Failed to get queued actions', { error: request.error });
        reject(request.error);
      };
    });
  }

  /**
   * Remove a specific queued action
   */
  async removeQueuedAction(id: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.QUEUED_ACTIONS], 'readwrite');
      const store = transaction.objectStore(STORES.QUEUED_ACTIONS);
      const request = store.delete(id);

      request.onsuccess = () => {
        logger.debug('Queued action removed', { id });
        resolve();
      };

      request.onerror = () => {
        logger.error('Failed to remove queued action', {
          error: request.error,
        });
        reject(request.error);
      };
    });
  }

  /**
   * Update retry count for an action
   */
  async updateActionRetryCount(id: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.QUEUED_ACTIONS], 'readwrite');
      const store = transaction.objectStore(STORES.QUEUED_ACTIONS);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const action = getRequest.result;
        if (action) {
          action.retryCount += 1;
          const updateRequest = store.put(action);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve();
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Clear all queued actions
   */
  async clearQueue(): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.QUEUED_ACTIONS], 'readwrite');
      const store = transaction.objectStore(STORES.QUEUED_ACTIONS);
      const request = store.clear();

      request.onsuccess = () => {
        logger.info('Action queue cleared');
        resolve();
      };

      request.onerror = () => {
        logger.error('Failed to clear queue', { error: request.error });
        reject(request.error);
      };
    });
  }

  /**
   * Save card data for offline use
   */
  async saveCardData(cards: CardsListResponse): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.CARD_DATA], 'readwrite');
      const store = transaction.objectStore(STORES.CARD_DATA);

      const data = {
        type: 'all-cards',
        cards,
        savedAt: Date.now(),
      };

      const request = store.put(data);

      request.onsuccess = () => {
        logger.info('Card data saved for offline use', {
          questions: cards.totals.questions,
          answers: cards.totals.answers,
          treatments: cards.totals.treatments,
        });

        // Also notify service worker
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CACHE_CARDS',
            cards,
          });
        }

        resolve();
      };

      request.onerror = () => {
        logger.error('Failed to save card data', { error: request.error });
        reject(request.error);
      };
    });
  }

  /**
   * Load card data from offline storage
   */
  async loadCardData(): Promise<CardsListResponse | null> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.CARD_DATA], 'readonly');
      const store = transaction.objectStore(STORES.CARD_DATA);
      const request = store.get('all-cards');

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          logger.debug('Card data loaded from offline storage', {
            savedAt: result.savedAt,
          });
          resolve(result.cards);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        logger.error('Failed to load card data', { error: request.error });
        reject(request.error);
      };
    });
  }

  /**
   * Save user preference
   */
  async savePreference(key: string, value: unknown): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.USER_PREFERENCES], 'readwrite');
      const store = transaction.objectStore(STORES.USER_PREFERENCES);

      const data: UserPreferences = {
        key,
        value,
        updatedAt: Date.now(),
      };

      const request = store.put(data);

      request.onsuccess = () => {
        logger.debug('Preference saved', { key });
        resolve();
      };

      request.onerror = () => {
        logger.error('Failed to save preference', { error: request.error });
        reject(request.error);
      };
    });
  }

  /**
   * Load user preference
   */
  async loadPreference<T>(key: string): Promise<T | null> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORES.USER_PREFERENCES], 'readonly');
      const store = transaction.objectStore(STORES.USER_PREFERENCES);
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result as UserPreferences | undefined;
        if (result) {
          resolve(result.value as T);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        logger.error('Failed to load preference', { error: request.error });
        reject(request.error);
      };
    });
  }

  /**
   * Clear all offline data
   */
  async clearAll(): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    const storeNames = Object.values(STORES);

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(storeNames, 'readwrite');

      let completed = 0;
      const totalStores = storeNames.length;

      storeNames.forEach(storeName => {
        const store = transaction.objectStore(storeName);
        const request = store.clear();

        request.onsuccess = () => {
          completed++;
          if (completed === totalStores) {
            logger.info('All offline data cleared');
            resolve();
          }
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    });
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(): Promise<{
    gameStates: number;
    queuedActions: number;
    hasCardData: boolean;
    preferences: number;
  }> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    const stats = {
      gameStates: 0,
      queuedActions: 0,
      hasCardData: false,
      preferences: 0,
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(Object.values(STORES), 'readonly');

      const gameStateStore = transaction.objectStore(STORES.GAME_STATE);
      const countRequest = gameStateStore.count();
      countRequest.onsuccess = () => {
        stats.gameStates = countRequest.result;
      };

      const actionsStore = transaction.objectStore(STORES.QUEUED_ACTIONS);
      const actionsRequest = actionsStore.count();
      actionsRequest.onsuccess = () => {
        stats.queuedActions = actionsRequest.result;
      };

      const cardStore = transaction.objectStore(STORES.CARD_DATA);
      const cardRequest = cardStore.get('all-cards');
      cardRequest.onsuccess = () => {
        stats.hasCardData = !!cardRequest.result;
      };

      const prefStore = transaction.objectStore(STORES.USER_PREFERENCES);
      const prefRequest = prefStore.count();
      prefRequest.onsuccess = () => {
        stats.preferences = prefRequest.result;
      };

      transaction.oncomplete = () => resolve(stats);
      transaction.onerror = () => reject(transaction.error);
    });
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorage();

// Export class for testing
export { OfflineStorage };
